import { actions, actionTypes, selectors } from 'data'
import { SetUserActionType } from 'data/actions'
import { ActionType } from 'data/types'
import isNil from 'lodash/isNil'
import { call, delay, put, select, takeEvery } from 'redux-saga/effects'
import { getCurrencies } from 'services/api/instrument'
import { saveSettings } from 'services/api/user'
import webSocket from 'services/api/webSocket'
import { logger, sanitizeMessage } from 'utils'

import { InstrumentType, SymbolMessageType, SymbolType, toSymbol } from './types'

const messageManager = function* (action) {
  const message: SymbolMessageType = action.payload

  if (message.event === webSocket.event.REJECTED) {
    logger.warn(JSON.stringify(message))
    return
  }

  if (message.event === webSocket.event.UPDATED) {
    yield put(actions.instrument.updateInstrument(sanitizeMessage(message) as InstrumentType))
  }
}

const start = function* () {
  const channel = webSocket.channel.SYMBOLS
  yield put(actions.socket.subscribe({ channel }))
}

const stop = function* () {
  yield put(
    actions.socket.unsubscribe({
      channel: webSocket.channel.SYMBOLS
    })
  )
}

const RE_FETCH_DELAY = 1000 * 5

const fetchCurrencies = function* () {
  const user = yield select(selectors.auth.getUser)
  if (!user) return // fetch curencies only when logged in

  const currencies = yield call(getCurrencies)
  if (!currencies.ok) {
    // Schedule another call if failed
    yield delay(RE_FETCH_DELAY)
    yield call(fetchCurrencies)
    return
  }

  yield put(actions.instrument.setCurrencies(currencies.response.currencies))
}

const saveFavorites = function* () {
  const authenticated: boolean = yield select(selectors.auth.isAuthenticated)
  if (!authenticated) {
    return
  }
  const favorites: SymbolType[] = yield select(selectors.instrument.getFavorites)
  yield call(saveSettings, {
    favorites: favorites.join(',')
  })
}

const setFavorites = function* (action: SetUserActionType) {
  const { user } = action.payload
  if (isNil(user)) return

  const { favorites } = user.settings ?? {}
  if (!favorites) return

  const parsedFavorites = favorites.split(',').map(toSymbol)
  yield put(actions.instrument.setFavorites(parsedFavorites))
}

export default function* instrumentMain() {
  yield takeEvery(actionTypes.auth.AUTHED, fetchCurrencies)
  yield takeEvery(actionTypes.auth.SET_USER, setFavorites)
  yield takeEvery(actionTypes.instrument.ADD_FAVORITE, saveFavorites)
  yield takeEvery(actionTypes.instrument.REMOVE_FAVORITE, saveFavorites)

  yield takeEvery(actionTypes.instrument.STOP, stop)
  yield takeEvery(actionTypes.socket.SET_CONNECTION, start)
  const channel = webSocket.channel.SYMBOLS
  yield takeEvery(
    (action: ActionType) =>
      action.type === actionTypes.socket.RECEIVED && action.payload.channel === channel,
    messageManager
  )
}
